import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import AlertMessageComponent from '../../components/AlertMessageComponent';
import { useAlert } from '../../components/alertContext';
import { useSearch } from '../../components/searchContext';
import HttpService from '../../services/HttpService';
import Constants from '../../utils/constants';
import { useStore } from '../../utils/store';

interface MatrixRow {
  documentTypeId: number;
  documentTypeName: string;
  labourId: number;
  labourName: string;
  documentStatus: string;
}

interface Labour {
  id: number;
  labourName: string;
}

const statusConfig: Record<
  string,
  { bg: string; color: string; icon: any; label: string }
> = {
  Current: {
    bg: '#E8F5E9',
    color: '#2E7D32',
    icon: <CheckCircleIcon sx={{ fontSize: 16, color: '#2E7D32' }} />,
    label: 'Current',
  },
  Missing: {
    bg: '#F5F5F5',
    color: '#616161',
    icon: <RemoveCircleOutlineIcon sx={{ fontSize: 16, color: '#616161' }} />,
    label: 'Missing',
  },
  Expired: {
    bg: '#FFEBEE',
    color: '#C62828',
    icon: <CancelIcon sx={{ fontSize: 16, color: '#C62828' }} />,
    label: 'Expired',
  },
  'Expires within 1 Month': {
    bg: '#FFF8E1',
    color: '#F57F17',
    icon: <WarningAmberIcon sx={{ fontSize: 16, color: '#F57F17' }} />,
    label: 'Expiring',
  },
};

const LabourSkillsMatrix: React.FC = () => {
  const { searchQuery } = useSearch();
  const isInitialLoad = useRef(true);
  const { getAdmin } = useStore();
  const { alert, setAlert, handleCloseAlert } = useAlert();
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const [documentTypes, setDocumentTypes] = useState<
    { id: number; name: string }[]
  >([]);
  const [labours, setLabours] = useState<Labour[]>([]);
  const [matrix, setMatrix] = useState<Record<string, Record<string, string>>>(
    {}
  );
  const [summary, setSummary] = useState({
    total: 0,
    compliant: 0,
    expired: 0,
    expiring: 0,
    missing: 0,
  });

  const fetchData = async () => {
    try {
      const params: any = {};
      if (searchQuery) params.search = searchQuery;

      const response = await HttpService.get(
        `${Constants.apiURL}${Constants.reportURL}/labour-skills-matrix`,
        { headers: { Authorization: `Bearer ${getAdmin()}` } },
        Object.keys(params).length > 0 ? params : null
      );

      if (response.success && response.data) {
        const data: MatrixRow[] = response.data;

        const docTypeMap = new Map<number, string>();
        data.forEach((row) =>
          docTypeMap.set(row.documentTypeId, row.documentTypeName)
        );
        const docTypes = Array.from(docTypeMap, ([id, name]) => ({
          id,
          name,
        }));

        const labourMap = new Map<number, Labour>();
        data.forEach((row) => {
          if (!labourMap.has(row.labourId)) {
            labourMap.set(row.labourId, {
              id: row.labourId,
              labourName: row.labourName,
            });
          }
        });
        const labourList = Array.from(labourMap.values());

        // Build matrix: labourId -> docTypeId -> status
        const matrixData: Record<string, Record<string, string>> = {};
        data.forEach((row) => {
          if (!matrixData[row.labourId]) {
            matrixData[row.labourId] = {};
          }
          matrixData[row.labourId][row.documentTypeId] = row.documentStatus;
        });

        // Calculate summary per labour
        let compliant = 0;
        let hasExpired = 0;
        let hasExpiring = 0;
        let hasMissing = 0;
        labourList.forEach((labour) => {
          const statuses = Object.values(matrixData[labour.id] || {});
          if (statuses.every((s) => s === 'Current')) compliant++;
          if (statuses.some((s) => s === 'Expired')) hasExpired++;
          if (statuses.some((s) => s === 'Expires within 1 Month'))
            hasExpiring++;
          if (statuses.some((s) => s === 'Missing')) hasMissing++;
        });

        setDocumentTypes(docTypes);
        setLabours(labourList);
        setMatrix(matrixData);
        setSummary({
          total: labourList.length,
          compliant,
          expired: hasExpired,
          expiring: hasExpiring,
          missing: hasMissing,
        });
      }
    } catch (error: any) {
      console.error('GET Error:', error);
      setAlert({ type: 'error', message: error.message, open: true });
    }
  };

  useEffect(() => {
    if (isInitialLoad.current) {
      fetchData();
      isInitialLoad.current = false;
      return;
    }
    fetchData();
  }, [searchQuery]);

  // Filter labours based on status filter
  const filteredLabours = labours.filter((labour) => {
    if (statusFilter === 'all') return true;
    const statuses = Object.values(matrix[labour.id] || {});
    if (statusFilter === 'compliant')
      return statuses.every((s) => s === 'Current');
    if (statusFilter === 'expired')
      return statuses.some((s) => s === 'Expired');
    if (statusFilter === 'expiring')
      return statuses.some((s) => s === 'Expires within 1 Month');
    if (statusFilter === 'missing')
      return statuses.some((s) => s === 'Missing');
    return true;
  });

  const summaryCards = [
    {
      label: 'Total Labourers',
      value: summary.total,
      color: '#000B29',
      bg: '#F5F5F5',
    },
    {
      label: 'Fully Compliant',
      value: summary.compliant,
      color: '#2E7D32',
      bg: '#E8F5E9',
    },
    {
      label: 'Has Expired',
      value: summary.expired,
      color: '#C62828',
      bg: '#FFEBEE',
    },
    {
      label: 'Expiring Soon',
      value: summary.expiring,
      color: '#F57F17',
      bg: '#FFF8E1',
    },
    {
      label: 'Missing',
      value: summary.missing,
      color: '#616161',
      bg: '#F5F5F5',
    },
  ];

  return (
    <Stack>
      <Box className="main dashboardmain">
        <section className="section dashboard">
          <div className="containergrid">
            <div className="mt-2">
              {/* Header */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Labour Skills Matrix
                </Typography>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Filter by Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Filter by Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Labourers</MenuItem>
                    <MenuItem value="compliant">Fully Compliant</MenuItem>
                    <MenuItem value="expired">Expired</MenuItem>
                    <MenuItem value="expiring">Expiring Soon</MenuItem>
                    <MenuItem value="missing">Missing</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Summary Cards - hidden per client request, set to true to re-enable */}
              {false && (
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                  {summaryCards.map((card) => (
                    <Paper
                      key={card.label}
                      elevation={0}
                      sx={{
                        flex: '1 1 160px',
                        p: 2,
                        backgroundColor: card.bg,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: card.color + '22',
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: '#666', fontSize: '11px' }}
                      >
                        {card.label}
                      </Typography>
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: 700, color: card.color, mt: 0.5 }}
                      >
                        {card.value}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              )}

              {/* Legend */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                {Object.entries(statusConfig).map(([key, cfg]) => (
                  <Chip
                    key={key}
                    icon={cfg.icon}
                    label={key}
                    size="small"
                    sx={{
                      backgroundColor: cfg.bg,
                      color: cfg.color,
                      fontWeight: 500,
                      fontSize: '12px',
                    }}
                  />
                ))}
              </Box>

              {/* Matrix Table */}
              <Paper elevation={0} sx={{ border: '1px solid #E0E0E0' }}>
                <TableContainer sx={{ maxHeight: 'calc(100vh - 380px)' }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            minWidth: 200,
                            position: 'sticky',
                            left: 0,
                            zIndex: 3,
                            backgroundColor: '#FAFAFA',
                            borderBottom: '2px solid #E0E0E0',
                          }}
                        >
                          Labourer
                        </TableCell>
                        {documentTypes.map((dt) => (
                          <TableCell
                            key={dt.id}
                            align="center"
                            sx={{
                              fontWeight: 600,
                              fontSize: '11px',
                              minWidth: 50,
                              maxWidth: 50,
                              backgroundColor: '#FAFAFA',
                              borderBottom: '2px solid #E0E0E0',
                              whiteSpace: 'normal',
                              lineHeight: 1.2,
                              p: '8px 4px',
                            }}
                          >
                            <Tooltip title={dt.name} arrow>
                              <span>{dt.name}</span>
                            </Tooltip>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredLabours.map((labour) => (
                        <TableRow key={labour.id} hover>
                          <TableCell
                            sx={{
                              fontWeight: 500,
                              position: 'sticky',
                              left: 0,
                              backgroundColor: '#fff',
                              zIndex: 1,
                              borderRight: '1px solid #E0E0E0',
                            }}
                          >
                            {labour.labourName}
                          </TableCell>
                          {documentTypes.map((dt) => {
                            const status =
                              matrix[labour.id]?.[dt.id] || 'Missing';
                            const cfg =
                              statusConfig[status] || statusConfig['Missing'];
                            return (
                              <TableCell
                                key={dt.id}
                                align="center"
                                sx={{ p: '4px' }}
                              >
                                <Tooltip title={`${dt.name}: ${status}`} arrow>
                                  <Box
                                    sx={{
                                      width: 28,
                                      height: 28,
                                      borderRadius: '6px',
                                      backgroundColor: cfg.bg,
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      border: `1px solid ${cfg.color}33`,
                                    }}
                                  >
                                    {cfg.icon}
                                  </Box>
                                </Tooltip>
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                      {filteredLabours.length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={documentTypes.length + 1}
                            align="center"
                            sx={{ py: 4, color: '#999' }}
                          >
                            No labourers found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              {/* Footer count */}
              <Box sx={{ mt: 1, textAlign: 'right' }}>
                <Typography variant="caption" sx={{ color: '#999' }}>
                  Showing {filteredLabours.length} of {labours.length} labourers
                </Typography>
              </Box>
            </div>
          </div>
        </section>
      </Box>
      <AlertMessageComponent
        type={alert.type}
        open={alert.open}
        handleClose={handleCloseAlert}
        message={alert.message}
      />
    </Stack>
  );
};

export default LabourSkillsMatrix;
